import {
  ArrowLeft,
  ArrowRight,
  ArrowSquareOut,
  BookmarkSimple,
  CaretLeft,
  CaretRight,
  Cards,
  ChatCircleText,
  Check,
  DotsThree,
  Flag,
  ListPlus,
  Pause,
  ShareNetwork,
  SpeakerHigh,
  TextAlignLeft,
  ThumbsDown,
  ThumbsUp,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Empty, Img, Modal, Skeleton, SourceLogo } from '@/components/primitives';
import { Footer } from '@/components/shell';
import { MiniStory, StoryMeta } from '@/components/stories';
import { ChatDrawer, Comments, ListPicker } from '@/components/story-extras';
import { fetchSimilar, fetchStory, parseSourceList, translateStory } from '@/lib/api/content';
import { finishRead, reportContent, startRead } from '@/lib/api/user';
import { useAuth } from '@/lib/auth';
import { hostOf, paragraphs, readMinutes, timeAgo } from '@/lib/format';
import { handleActionError, isTypingTarget, useDocumentTitle, useMyStates, useRequireAuth } from '@/lib/hooks';
import { useT, useUiLang } from '@/lib/i18n';
import { player, usePlayer } from '@/lib/player';
import { setPrefs, usePrefs } from '@/lib/prefs';
import { chordActive } from '@/lib/keys';
import { categoryColor, categoryLabel, subcategoryLabel } from '@/lib/taxonomy';
import type { ContentCard, Story as StoryT } from '@/lib/types';
import { ui } from '@/lib/ui';

export default function Story() {
  const { id = '' } = useParams();
  const t = useT();
  const lang = useUiLang();
  const nav = useNavigate();
  const { user, premium, contentLang } = useAuth();
  const prefs = usePrefs();
  const require = useRequireAuth();
  const p = usePlayer();

  const q = useQuery({ queryKey: ['story', id, contentLang], queryFn: () => fetchStory(id, contentLang) });
  const similarQ = useQuery({ queryKey: ['similar', id, contentLang], queryFn: () => fetchSimilar(id, contentLang, 5), enabled: !!q.data });
  const states = useMyStates(id ? [id] : []);
  const my = states.get(id);

  const [chat, setChat] = useState(false);
  const [lists, setLists] = useState(false);
  const [more, setMore] = useState(false);
  const mode = prefs.storyMode;

  const d = q.data;
  const cover = d?.cards.find((c) => c.kind === 'cover');
  const bodies = useMemo(() => (d?.cards ?? []).filter((c) => c.kind === 'body'), [d]);
  const sourceList = d?.cards.find((c) => c.kind === 'source_list');
  const title = cover?.heading?.trim() || d?.content.title || '';
  const dek = cover?.body?.trim() || null;
  const color = categoryColor(d?.category?.slug);
  useDocumentTitle(title || null);

  // Premium kullanıcı başka dil seçtiyse ve kart yoksa bir kez çevir.
  const translatedRef = useRef(false);
  useEffect(() => {
    if (!d || translatedRef.current) return;
    if (premium && d.cardLang !== contentLang && contentLang !== 'en') {
      translatedRef.current = true;
      translateStory(id, contentLang)
        .then(() => q.refetch())
        .catch(() => undefined);
    }
  }, [d, premium, contentLang, id, q]);

  // Okuma kaydı: açılışta satır, çıkışta süre + görülen kart.
  const maxSeen = useRef(1);
  const uid = user?.id;
  useEffect(() => {
    if (!uid || !id) return;
    let readId: string | null = null;
    const started = Date.now();
    maxSeen.current = 1;
    let cancelled = false;
    startRead(uid, id).then((r) => {
      if (cancelled && r) void finishRead(r, (Date.now() - started) / 1000, maxSeen.current);
      readId = r;
    });
    return () => {
      cancelled = true;
      if (readId) void finishRead(readId, (Date.now() - started) / 1000, maxSeen.current);
    };
  }, [uid, id]);

  const isPlaying = p.playing && player.isCurrent(id);
  const listen = useCallback(() => {
    if (player.isCurrent(id)) player.toggle();
    else if (d) player.play([{ contentId: id, title, image: d.content.cover_image_url }]);
  }, [id, d, title]);

  const share = async () => {
    const url = `${location.origin}/s/${id}`;
    try {
      if (navigator.share) await navigator.share({ title, text: dek ?? undefined, url });
      else {
        await navigator.clipboard.writeText(url);
        ui.toast(t('story_copied'));
      }
    } catch {
      // paylaşım iptal edildi
    }
  };

  // Sayfa kısayolları (okuyucunun ←/→ kısayolları kendi içinde).
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('[role="dialog"]') || chordActive() || e.key === 'g') return;
      const k = e.key.toLowerCase();
      if (k === 'l') listen();
      else if (k === 's') states.toggleSave(id);
      else if (k === 'a') require(() => setChat(true));
      else if (k === 'f') setPrefs({ storyMode: mode === 'cards' ? 'full' : 'cards' });
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [listen, states, id, require, mode]);

  if (q.isLoading) return <StorySkeleton />;
  if (!d)
    return (
      <div className="page">
        <Empty
          title={t('story_not_found')}
          action={
            <Link className="btn btn-soft" to="/">
              {t('go_home')}
            </Link>
          }
        />
      </div>
    );

  const sourceRows =
    d.members.length > 0
      ? d.members.map((m) => ({ key: m.id, name: m.source?.title ?? hostOf(m.url), title: m.title, url: m.url, logo: m.source?.logo_url, site: m.source?.site_url ?? m.url, when: m.published_at }))
      : sourceList && d.content.is_synthesis
        ? parseSourceList(sourceList.body).map((s, i) => ({ key: String(i), name: s.name, title: s.title, url: s.url, logo: null, site: s.url, when: null }))
        : [];
  const facts = (
    [
      ['story_what', d.content.k_what],
      ['story_who', d.content.k_who],
      ['story_where', d.content.k_where],
      ['story_when', d.content.k_when],
      ['story_why', d.content.k_why],
      ['story_how', d.content.k_how],
    ] as const
  ).filter(([, v]) => v);
  const asStory: StoryT = { ...d.content, heading: title, dek };
  const nextStory = similarQ.data?.[0];

  return (
    <div style={{ '--c': color } as CSSProperties}>
      <div className="page">
        <div className="story-wrap">
          <article>
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: -12, marginBottom: 12 }} onClick={() => (history.length > 1 ? nav(-1) : nav('/'))}>
              <ArrowLeft size={16} /> {t('story_back')}
            </button>
            <header className="story-head">
              <StoryMeta story={asStory} />
              <h1 className="story-title">{title}</h1>
              {dek && <p className="story-dek">{dek}</p>}
              <div className="meta" style={{ marginTop: 16 }}>
                {d.source && !d.content.is_synthesis && (
                  <Link to={`/source/${d.source.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-2)' }}>
                    <SourceLogo src={d.source.logo_url} site={d.source.site_url} />
                    {d.source.title}
                  </Link>
                )}
                {sourceRows.length > 0 && <span>{sourceRows.length === 1 ? t('story_source_one') : t('story_sources', { n: sourceRows.length })}</span>}
                <span className="meta-sep" />
                <span>{t('story_read_min', { n: readMinutes(d.content.summary_word_count) })}</span>
              </div>
              {d.content.cover_image_url && <Img className="story-cover" src={d.content.cover_image_url} color={color} eager width={900} sizes="(max-width: 999px) 100vw, 760px" />}
            </header>

            <div className="story-bar" role="toolbar" aria-label={title}>
              <div className="seg">
                <button className={mode === 'cards' ? 'active' : ''} onClick={() => setPrefs({ storyMode: 'cards' })} aria-pressed={mode === 'cards'} aria-label={t('story_cards')}>
                  <Cards size={16} /> <span className="label-sm">{t('story_cards')}</span>
                </button>
                <button className={mode === 'full' ? 'active' : ''} onClick={() => setPrefs({ storyMode: 'full' })} aria-pressed={mode === 'full'} aria-label={t('story_full')}>
                  <TextAlignLeft size={16} /> <span className="label-sm">{t('story_full')}</span>
                </button>
              </div>
              <span className="spacer" />
              <button className={`btn btn-sm ${isPlaying ? 'on' : 'btn-ghost'}`} onClick={listen} title={`${t('story_listen')} (L)`}>
                {isPlaying ? <Pause size={18} weight="fill" /> : <SpeakerHigh size={18} />}
                <span className="label-sm">{isPlaying ? t('story_pause') : t('story_listen')}</span>
              </button>
              <button className="btn btn-sm btn-ghost" onClick={() => require(() => setChat(true))} title={`${t('story_ask')} (A)`}>
                <ChatCircleText size={18} />
                <span className="label-sm">{t('story_ask')}</span>
              </button>
              <button className={`icon-btn${my.saved ? ' on' : ''}`} onClick={() => states.toggleSave(id)} aria-pressed={my.saved} aria-label={t('story_save')} title={`${t('story_save')} (S)`}>
                <BookmarkSimple size={19} weight={my.saved ? 'fill' : 'regular'} />
              </button>
              <button className="icon-btn" onClick={() => require(() => (premium ? setLists(true) : ui.openPremium('lists')))} aria-label={t('story_add_list')} title={t('story_add_list')}>
                <ListPlus size={19} />
              </button>
              <button className="icon-btn" onClick={share} aria-label={t('story_share')} title={t('story_share')}>
                <ShareNetwork size={19} />
              </button>
              <button className={`icon-btn up${my.vote === 1 ? ' on' : ''}`} onClick={() => states.vote(id, 1)} aria-pressed={my.vote === 1} aria-label={t('story_useful')} title={t('story_useful')}>
                <ThumbsUp size={19} weight={my.vote === 1 ? 'fill' : 'regular'} />
              </button>
              <button className={`icon-btn down${my.vote === -1 ? ' on' : ''}`} onClick={() => states.vote(id, -1)} aria-pressed={my.vote === -1} aria-label={t('story_not_useful')} title={t('story_not_useful')}>
                <ThumbsDown size={19} weight={my.vote === -1 ? 'fill' : 'regular'} />
              </button>
              <button className="icon-btn" onClick={() => setMore(true)} aria-label="…" aria-haspopup="dialog">
                <DotsThree size={20} weight="bold" />
              </button>
            </div>

            {mode === 'cards' ? (
              <CardReader
                key={id}
                cards={bodies}
                onSeen={(n) => (maxSeen.current = Math.max(maxSeen.current, n + 1))}
                next={nextStory}
              />
            ) : (
              <div className="fulltext">
                {bodies.map((c, i) => (
                  <section key={c.id}>
                    {c.heading && (
                      <h2>
                        <span className="lead-num">{i + 1}</span>
                        {c.heading}
                      </h2>
                    )}
                    {paragraphs(c.body).map((para, k) => (
                      <p key={k}>{para}</p>
                    ))}
                  </section>
                ))}
              </div>
            )}

            <Comments contentId={id} />
          </article>

          <aside className="aside">
            {sourceRows.length > 0 ? (
              <div className="box">
                <div className="box-title">
                  {t('story_covered_by')} <span>{sourceRows.length}</span>
                </div>
                {sourceRows.slice(0, 8).map((s) => (
                  <a key={s.key} className="src-row" href={s.url ?? '#'} target="_blank" rel="noreferrer">
                    <SourceLogo src={s.logo} site={s.site} />
                    <div style={{ minWidth: 0 }}>
                      <strong>
                        {s.name}
                        {s.when && <span className="muted" style={{ display: 'inline', fontWeight: 500, fontSize: 12 }}> · {timeAgo(s.when)}</span>}
                      </strong>
                      <span className="clamp-2">{s.title}</span>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <div className="box">
                <div className="box-title">{t('story_original')}</div>
                <a className="src-row" href={d.content.url} target="_blank" rel="noreferrer">
                  <SourceLogo src={d.source?.logo_url} site={d.source?.site_url ?? d.content.url} />
                  <div style={{ minWidth: 0 }}>
                    <strong>{d.source?.title ?? hostOf(d.content.url)}</strong>
                    <span className="clamp-2">{d.content.title}</span>
                  </div>
                </a>
              </div>
            )}

            {facts.length > 0 && (
              <div className="box">
                <div className="box-title">{t('story_5w1h')}</div>
                <dl className="facts">
                  {facts.map(([k, v]) => (
                    <div key={k} style={{ display: 'contents' }}>
                      <dt>{t(k)}</dt>
                      <dd>{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {d.tags.length > 0 && (
              <div className="box">
                <div className="box-title">{t('story_tags')}</div>
                <div className="chips chips-wrap">
                  {d.tags.map((tag) => (
                    <Link key={tag.categoryId + (tag.subSlug ?? '')} to={`/c/${tag.categorySlug}`} className="chip chip-sm">
                      <span className="dot" style={{ '--c': categoryColor(tag.categorySlug) } as CSSProperties} />
                      {tag.subSlug ? subcategoryLabel(lang, tag.subSlug, tag.subName ?? '') : categoryLabel(lang, tag.categorySlug, tag.categoryName)}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {(similarQ.data?.length ?? 0) > 0 && (
              <div className="box">
                <div className="box-title">{t('story_related')}</div>
                {similarQ.data!.map((s) => (
                  <MiniStory key={s.id} story={s} />
                ))}
              </div>
            )}
          </aside>
        </div>
        <Footer />
      </div>

      {chat && <ChatDrawer contentId={id} title={title} onClose={() => setChat(false)} />}
      {lists && <ListPicker contentId={id} onClose={() => setLists(false)} />}
      {more && (
        <Modal onClose={() => setMore(false)}>
          <h2 style={{ fontSize: 17, marginBottom: 8 }} className="clamp-2">
            {title}
          </h2>
          {!d.content.is_synthesis && (
            <a className="menu-item" href={d.content.url} target="_blank" rel="noreferrer" onClick={() => setMore(false)}>
              <ArrowSquareOut size={18} /> {t('story_original')}
            </a>
          )}
          <button className="menu-item" onClick={() => { setMore(false); void share(); }}>
            <ShareNetwork size={18} /> {t('story_share')}
          </button>
          <button
            className="menu-item"
            onClick={() =>
              require((uid) => {
                setMore(false);
                reportContent(uid, id)
                  .then(() => ui.toast(t('story_reported')))
                  .catch(handleActionError);
              })
            }
          >
            <Flag size={18} /> {t('story_report')}
          </button>
        </Modal>
      )}
    </div>
  );
}

function CardReader({ cards, onSeen, next }: { cards: ContentCard[]; onSeen: (i: number) => void; next?: StoryT }) {
  const t = useT();
  const [i, setI] = useState(0);
  const [dir, setDir] = useState<'fwd' | 'back'>('fwd');
  const total = cards.length;
  const done = i >= total;
  const startX = useRef<number | null>(null);

  const go = useCallback(
    (n: number) => {
      const clamped = Math.max(0, Math.min(total, n));
      setDir(clamped >= i ? 'fwd' : 'back');
      setI(clamped);
      onSeen(Math.min(clamped, total - 1));
    },
    [i, total, onSeen]
  );

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target) || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (document.querySelector('[role="dialog"]')) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(i + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(i - 1);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [go, i]);

  if (total === 0) return null;
  const card = cards[Math.min(i, total - 1)];

  return (
    <div className="reader">
      <div className="reader-progress" role="tablist" aria-label={t('story_cards')}>
        {cards.map((c, k) => (
          <button
            key={c.id}
            role="tab"
            aria-selected={k === i}
            aria-label={t('story_card_of', { i: k + 1, n: total })}
            className={k === i ? 'current' : k < i ? 'seen' : ''}
            onClick={() => go(k)}
          />
        ))}
      </div>
      <div
        className="reader-card"
        onPointerDown={(e) => (startX.current = e.clientX)}
        onPointerUp={(e) => {
          if (startX.current == null) return;
          const dx = e.clientX - startX.current;
          startX.current = null;
          if (Math.abs(dx) > 50) go(dx < 0 ? i + 1 : i - 1);
        }}
      >
        {done ? (
          <div className="reader-done reader-anim">
            <div className="ring-check">
              <Check size={30} weight="bold" />
            </div>
            <h2 style={{ marginTop: 0 }}>{t('story_done')}</h2>
            {next && (
              <Link to={`/s/${next.id}`} className="btn btn-primary btn-lg" style={{ marginTop: 20 }}>
                {t('story_next_story')} <ArrowRight size={18} />
              </Link>
            )}
          </div>
        ) : (
          <div key={i} className={`reader-anim${dir === 'back' ? ' back' : ''}`} aria-live="polite">
            <div className="kicker">{t('story_card_of', { i: i + 1, n: total })}</div>
            {card.heading && <h2>{card.heading}</h2>}
            <div className="body">
              {paragraphs(card.body).map((para, k) => (
                <p key={k}>{para}</p>
              ))}
            </div>
          </div>
        )}
        <div className="reader-nav">
          <span className="count">
            {Math.min(i + 1, total)} / {total}
          </span>
          <button className="icon-btn" onClick={() => go(i - 1)} disabled={i === 0} aria-label={t('story_prev')}>
            <CaretLeft size={20} weight="bold" />
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => go(i + 1)} disabled={done} aria-label={t('story_next')}>
            <CaretRight size={18} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

function StorySkeleton() {
  return (
    <div className="page">
      <div className="story-wrap">
        <div>
          <Skeleton h={14} w={180} style={{ marginTop: 52 }} />
          <Skeleton h={40} style={{ marginTop: 18 }} />
          <Skeleton h={40} w="70%" style={{ marginTop: 10 }} />
          <Skeleton h={18} style={{ marginTop: 22 }} />
          <Skeleton h={18} w="85%" style={{ marginTop: 8 }} />
          <Skeleton h={360} r={24} style={{ marginTop: 28 }} />
        </div>
        <div style={{ display: 'grid', gap: 16, marginTop: 52 }}>
          <Skeleton h={220} r={16} />
          <Skeleton h={160} r={16} />
        </div>
      </div>
    </div>
  );
}
